/**
 * Component: SSE Stream Client
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R17
 *
 * Server-Sent Event client for the widget's streaming
 * endpoints (``POST /api/chat/stream``; future Galen
 * Search streaming endpoints follow the same protocol).
 *
 * The built-in browser ``EventSource`` is a poor fit here
 * because:
 *
 *   - EventSource only supports GET; the chat stream
 *     endpoint is a POST carrying the prompt body.
 *   - EventSource cannot set custom headers, which means
 *     no ``Authorization: Bearer <token>``.
 *
 * Instead we use ``fetch`` with the ``ReadableStream`` body
 * reader, parse the SSE framing ourselves, and yield
 * tokens to the caller via the ``onToken`` callback. This
 * keeps the R06 Bearer guarantee (every request carries
 * the JWT) intact on the streaming path too.
 *
 * SSE framing recap (RFC 8895-ish). The Galen backend
 * emits ``data:``-only frames where the event TYPE is a
 * field of the JSON body, NOT a ``event:`` framing line:
 *
 *     data: {"event":"start","safety_decision":"allow",
 *            "conversation_id":"...","done":false}
 *
 *     data: {"event":"chunk","content":"The ESC ",
 *            "cumulative":"The ESC ","done":false}
 *
 *     data: {"event":"done","cumulative":"...",
 *            "citations":[...],"is_grounded":true,
 *            "message_id":"...","done":true}
 *
 * Events are separated by a blank line. The parser here
 * stays SSE-generic — it handles the field names a
 * conforming server MAY emit (``event:``, ``data:``,
 * ``id:``, ``retry:``) and defers the body-event
 * dispatch to the consumer (chat.tsx). When the server
 * omits ``event:`` (the backend's case) the parser
 * defaults ``event`` to ``"message"`` per the SSE spec;
 * the chat surface then JSON-parses ``data`` and
 * switches on the body's ``event`` field. AB#4384 fixed
 * the widget's chat surface to use this dispatch shape.
 */

import {
  WidgetAuthError,
  WidgetNetworkError,
  WidgetRateLimitError,
  parseRetryAfterHeader,
} from './errors';

/**
 * Event shape yielded to callers. ``event`` is the SSE
 * event-type field (``token``, ``done``, ``error``, ...)
 * and ``data`` is the raw JSON string the backend sent;
 * callers parse it into their domain type.
 */
export interface SseEvent {
  event: string;
  data: string;
}

/**
 * Callback set a streaming call hands to the SSE client.
 * Every callback is optional; a caller that only cares
 * about tokens can omit ``onDone`` etc.
 *
 * ``onToken`` receives parsed SSE events in the order the
 * backend emitted them. The caller is responsible for
 * decoding ``event.data`` as JSON (or whatever shape the
 * specific endpoint emits) and updating UI state.
 *
 * ``onDone`` fires exactly once when the stream closes
 * cleanly (either via an ``event: done`` frame or via the
 * underlying connection closing). ``onError`` fires once
 * if the connection fails mid-stream — callers that care
 * about resumable streams can restart by calling
 * ``streamPostJson`` again.
 */
export interface SseCallbacks {
  onToken?: (event: SseEvent) => void;
  onDone?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Configuration for the stream client. ``baseUrl`` and
 * ``token`` mirror ``WidgetApiClient`` so both HTTP and
 * streaming paths share the same auth material. The
 * stream path does not participate in the R08 401-retry
 * flow because resuming a mid-stream refresh is
 * fundamentally racy; 401 from a stream surfaces as
 * ``WidgetAuthError`` on the caller, who can trigger a
 * fresh ``streamPostJson`` after handling the refresh.
 */
export interface StreamClientConfig {
  baseUrl: string;
  token: string;
}

/**
 * Open a streaming POST to ``path``. Returns an
 * ``AbortController`` the caller can ``abort()`` to cancel
 * the stream (the underlying fetch receives the signal and
 * closes the connection cleanly).
 */
export async function streamPostJson(
  config: StreamClientConfig,
  path: string,
  body: unknown,
  callbacks: SseCallbacks,
): Promise<AbortController> {
  const controller = new AbortController();
  const url = resolveUrl(config.baseUrl, path);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${config.token}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    callbacks.onError?.(
      new WidgetNetworkError(
        `SSE stream request to ${url} failed: ${e instanceof Error ? e.message : String(e)}`,
        e,
      ),
    );
    return controller;
  }

  if (response.status === 401) {
    callbacks.onError?.(
      new WidgetAuthError(
        `SSE stream to ${url} returned HTTP 401. The host should refresh ` +
          'the JWT (via onAuthRefresh) and restart the stream.',
      ),
    );
    return controller;
  }

  // WIDGET-V1-R19: rate-limit responses on streaming calls
  // surface as WidgetRateLimitError with retry-after metadata
  // so the chat surface can render the documented "please
  // wait N seconds" message.
  if (response.status === 429) {
    const retryAfter = parseRetryAfterHeader(
      response.headers.get('Retry-After'),
    );
    callbacks.onError?.(
      new WidgetRateLimitError(
        `SSE stream to ${url} was rate-limited (HTTP 429).` +
          (retryAfter !== null ? ` Retry after ${retryAfter}s.` : ''),
        retryAfter,
      ),
    );
    return controller;
  }

  if (!response.ok || !response.body) {
    callbacks.onError?.(
      new WidgetNetworkError(
        `SSE stream to ${url} returned HTTP ${response.status}`,
        null,
      ),
    );
    return controller;
  }

  // Drive the reader off the event loop so the caller gets
  // the AbortController handle back synchronously.
  void pumpStream(response.body, callbacks);
  return controller;
}

async function pumpStream(
  body: ReadableStream<Uint8Array>,
  callbacks: SseCallbacks,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Events are separated by a blank line; extract each
      // complete event from the buffer and emit it.
      let separatorIndex = buffer.indexOf('\n\n');
      while (separatorIndex !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        const parsed = parseSseEvent(rawEvent);
        if (parsed) {
          callbacks.onToken?.(parsed);
          if (parsed.event === 'done') {
            callbacks.onDone?.();
            return;
          }
        }
        separatorIndex = buffer.indexOf('\n\n');
      }
    }
    // Stream closed cleanly without an explicit ``done``
    // event — still signal completion so the caller can
    // tear down its UI.
    callbacks.onDone?.();
  } catch (e) {
    callbacks.onError?.(
      new WidgetNetworkError(
        `SSE stream pump failed: ${e instanceof Error ? e.message : String(e)}`,
        e,
      ),
    );
  }
}

/**
 * Parse a single SSE event block (``event: X\ndata: Y``)
 * into an ``SseEvent``. Tolerates multi-line ``data:``
 * fields (the backend joins them with ``\n``) and ignores
 * unknown field prefixes so the parser stays forward-
 * compatible with backend changes.
 */
export function parseSseEvent(block: string): SseEvent | null {
  const lines = block.split(/\r?\n/);
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith(':') || line.trim() === '') {
      continue;
    }
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;
    const field = line.slice(0, colonIndex).trim();
    // Drop the single leading space the spec allows.
    let value = line.slice(colonIndex + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') {
      event = value;
    } else if (field === 'data') {
      dataLines.push(value);
    }
    // Other fields (``id``, ``retry``) are spec-valid but
    // unused by the chat stream; ignore them rather than
    // raising.
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

function resolveUrl(baseUrl: string, path: string): string {
  const trimmed = baseUrl.replace(/\/+$/, '');
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const normalised = path.startsWith('/') ? path : `/${path}`;
  return `${trimmed}${normalised}`;
}
